/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect } from 'react';
import { AppSettings } from './../../config/app-settings.js';
import { destroyScrollbars, initializeScrollbars } from './../../composables/scrollBar.js';
import Highlight from 'react-highlight';

const aiCodeExample = `<script>
  export default {
    async mounted() {
      const response = await fetch('https://api.example.com/data');
      const data = await response.json();
      console.log(data);
    }
  }
</script>`;

function AIChat() {
	const context = useContext(AppSettings);

	useEffect(() => {
		context.handleSetAppContentClass('p-0 d-flex position-relative bg-body h-100');
		context.handleSetAppContentFullHeight(true);
		context.handleSetAppSidebarMinified(true);
		return () => {
			context.handleSetAppContentClass('');
			context.handleSetAppContentFullHeight(false);
			context.handleSetAppSidebarMinified(false);
		};
	}, [context]);

	useEffect(() => {
		const responses = [
			"That's an interesting question! Let me think...",
			'Good point! What else do you think about this?',
			'That makes sense. Can you clarify a bit more?',
			'Great perspective! Here’s my take on it...',
			'Good question! I believe the answer is...',
			'I appreciate your input! Let’s explore this further.',
			"That's a unique thought! Here’s another angle to consider...",
			'Nice one! Have you considered looking at it from this perspective?'
		];
		const timers = [];
		const toggleTab = (name) => {
			['aiChatIntro', 'aiChatNew', 'aiChatPrev'].forEach((tabName) => {
				const tab = document.getElementById(tabName);
				if (tab) tab.classList.toggle('show', tabName === name);
				if (tab) tab.classList.toggle('active', tabName === name);
			});
		};
		const sendMessage = (value) => {
			const input = String(value || '').trim();
			const messages = document.getElementById('aiChatNewContent');
			if (!input || !messages) return;
			toggleTab('aiChatNew');
			const userRow = document.createElement('div');
			userRow.className = 'd-flex justify-content-end align-items-end mb-3';
			const bubble = document.createElement('div');
			bubble.className = 'rounded-4 px-3 py-2 bg-component text-body fw-bold';
			bubble.textContent = input;
			const avatar = document.createElement('div');
			avatar.innerHTML =
				'<div class="w-30px h-30px my-2px ms-2 fs-20px me-2 rounded-circle text-white bg-theme-gradient d-flex align-items-center justify-content-center">S</div>';
			userRow.append(bubble, avatar);
			messages.appendChild(userRow);
			const spinner = document.createElement('div');
			spinner.className = 'd-flex justify-content-start mb-3 ai-spinner';
			spinner.innerHTML = '<div class="px-2"><div class="spinner-grow spinner-grow-sm"></div></div>';
			messages.appendChild(spinner);
			const timer = window.setTimeout(() => {
				spinner.remove();
				const response = document.createElement('div');
				response.className = 'mb-3';
				response.innerHTML =
					'<div class="d-flex justify-content-start align-items-end"><div class="rounded-4 px-3 py-2 bg-component text-body fw-bold mw-75"><div class="typing-animation"></div></div></div>';
				const typingText = response.querySelector('.typing-animation');
				typingText.textContent = responses[Math.floor(Math.random() * responses.length)];
				messages.appendChild(response);
				typingText.style.animation = 'typing .25s steps(20, end), blink-caret 0.75s step-end infinite';
				typingText.addEventListener('animationend', () => {
					typingText.style.animation = 'none';
				});
			}, 2000);
			timers.push(timer);
		};
		const onClick = (event) => {
			const trigger = event.target.closest && event.target.closest('[data-toggle-ai-tab]');
			if (trigger) {
				event.preventDefault();
				const tabName = trigger.getAttribute('data-toggle-ai-tab');
				const prompt = trigger.getAttribute('data-toggle-ai-input');
				if (prompt) document.getElementById('aiChatNewContent')?.replaceChildren();
				toggleTab(tabName);
				if (prompt) sendMessage(prompt);
				return;
			}
			if (event.target.closest && event.target.closest('#sendButton')) {
				event.preventDefault();
				const input = document.getElementById('userInput');
				if (input) {
					sendMessage(input.value);
					input.value = '';
				}
			}
		};
		const onKeyDown = (event) => {
			if (event.key === 'Enter' && event.target.id === 'userInput') {
				event.preventDefault();
				sendMessage(event.target.value);
				event.target.value = '';
			}
		};
		document.addEventListener('click', onClick);
		document.addEventListener('keydown', onKeyDown);
		const content = document.getElementById('content');
		const scrollbars = initializeScrollbars(content || document);
		return () => {
			document.removeEventListener('click', onClick);
			document.removeEventListener('keydown', onKeyDown);
			timers.forEach((timer) => window.clearTimeout(timer));
			destroyScrollbars(scrollbars);
		};
	}, []);

	return (
		<>
			<a
				className="d-lg-none btn btn-icon btn-lg bg-component text-body rounded-0 z-3 rounded-end-3 mt-5 shadow-lg position-absolute top-0 start-0 "
				data-bs-toggle="offcanvas"
				href="#aiChatSidebar"
				role="button"
				aria-controls="offcanvasExample"
			>
				<i className="fa fa-gear"></i>
			</a>
			<div
				className="w-300px bg-light offcanvas offcanvas-start position-lg-relative transform-lg-none visibility-lg-initial z-lg-3"
				id="aiChatSidebar"
			>
				<div className="d-lg-none d-block position-absolute top-0 end-0 w-100 h-100"></div>
				<div className="position-relative h-100 d-flex flex-column">
					<div className="p-3 px-3 d-flex flex-column gap-2">
						<div className="position-relative">
							<input
								type="text"
								className="form-control ps-35px bg-component fw-semibold"
								placeholder="Search..."
							/>
							<button
								type="button"
								className="btn position-absolute px-10px start-0 top-0 bottom-0 d-flex align-items-center"
							>
								<i className="fa fa-search"></i>
							</button>
						</div>
					</div>
					<hr className="m-0" />
					<div className="flex-1 overflow-hidden text-body">
						<div data-scrollbar="true" className="p-3" data-height="100%">
							<div className="fw-bold text-secondary text-opacity-75 small mb-1 d-flex align-items-center">
								Today
							</div>
							<div className="small mb-3">
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Plan My Dream Vacation</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Craft a Winning Pitch</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Organize My Day</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Write Custom Code</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Draft a Polished Email</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Brainstorm Ideas</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
							</div>
							<div className="fw-bold text-secondary text-opacity-75 small mb-1 d-flex align-items-center">
								1 Week Ago
							</div>
							<div className="small mb-3">
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Summarize an Article</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Prepare for a Speech</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Create a Business Proposal</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
							</div>
							<div className="fw-bold text-secondary text-opacity-75 small mb-1 d-flex align-items-center">
								2 Weeks Ago
							</div>
							<div className="small mb-3">
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Organize Project Tasks</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Research Market Trends</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
								<div className="position-relative hover-bg-secondary bg-opacity-15 hover-show rounded py-6px px-3 border-0 d-flex align-items-center">
									<div className="fw-bold flex-1 text-body">Craft Social Media Posts</div>
									<a href="#/" className="stretched-link" data-toggle-ai-tab="aiChatPrev"></a>
									<div className="dropdown position-relative z-3">
										<a
											href="#/"
											data-bs-toggle="dropdown"
											className="link-secondary opacity-50 d-none hover-show-elm"
										>
											<i className="fa fa-ellipsis"></i>
										</a>
										<div className="dropdown-menu">
											<a href="#/" className="dropdown-item">
												Share Chat
											</a>
											<a href="#/" className="dropdown-item">
												See Details
											</a>
											<div className="dropdown-divider"></div>
											<a href="#/" className="dropdown-item">
												Rename
											</a>
											<a href="#/" className="dropdown-item">
												Archive
											</a>
											<a href="#/" className="dropdown-item">
												Delete
											</a>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<hr className="m-0" />
					<div className="small bg-light p-2 d-flex flex-column gap-1">
						<a
							href="#/"
							className="fw-bold text-decoration-none text-dark d-flex gap-2 align-items-center position-relative hover-bg-white hover-show rounded p-6px d-flex align-items-center"
							data-toggle-ai-tab="aiChatIntro"
						>
							<iconify-icon
								icon="solar:chat-line-bold-duotone"
								className="my-n2 fs-3 opacity-8"
							></iconify-icon>
							New Chat
						</a>
						<a
							href="#/"
							className="fw-bold text-decoration-none text-dark d-flex gap-2 align-items-center position-relative hover-bg-white hover-show rounded p-6px d-flex align-items-center"
							data-toggle-ai-tab="aiChatIntro"
						>
							<iconify-icon
								icon="solar:folder-with-files-bold-duotone"
								className="my-n2 fs-3 opacity-8"
							></iconify-icon>
							Explore Bots
						</a>
						<a
							href="#/"
							className="fw-bold text-decoration-none text-dark d-flex gap-2 align-items-center position-relative hover-bg-white hover-show rounded p-6px d-flex align-items-center"
							data-toggle-ai-tab="aiChatIntro"
						>
							<iconify-icon
								icon="solar:crown-star-bold-duotone"
								className="my-n2 fs-3 opacity-8"
							></iconify-icon>
							Go Pro
						</a>
					</div>
					<hr className="m-0" />
					<div className="px-3 p-3 bg-light">
						<div className="fw-bold flex-1 fs-13px text-body d-flex align-items-center">
							<div className="w-30px h-30px fs-20px me-2 my-n1 rounded-circle text-white bg-theme-gradient d-flex align-items-center justify-content-center">
								S
							</div>
							support@seantheme.com
						</div>
					</div>
				</div>
			</div>
			<div className="flex-1 d-flex flex-column bg-body">
				<div className="flex-1 overflow-hidden tab-content w-100">
					<div id="aiChatIntro" className="h-100 tab-pane fade show active">
						<div data-scrollbar="true" data-height="100%" className="px-3 px-lg-5 py-4">
							<div className="fs-1 fw-bold mb-0">Hello, there</div>
							<div className="fs-5 fw-semibold text-body mb-4">How can I assist you today?</div>
							<div className="row g-3">
								<div className="col-sm-6 col-xl-3">
									<a
										className="card cursor-pointer h-100 border-0 bg-component hover-bg-light position-relative overflow-hidden rounded-4 text-decoration-none"
										data-toggle-ai-tab="aiChatNew"
										data-toggle-ai-input="Suggest hidden gems and must-see spots for my next trip."
									>
										<div className="card-body p-4">
											<div className="mb-3 d-flex">
												<iconify-icon
													icon="solar:calendar-date-bold-duotone"
													className="fs-40px text-body"
												></iconify-icon>
											</div>
											<div className="text-secondary fw-bold small">Plan My Adventure</div>
											<div className="fs-6 fw-bold">
												Suggest hidden gems and must-see spots for my next trip.
											</div>
										</div>
									</a>
								</div>
								<div className="col-sm-6 col-xl-3">
									<a
										className="card cursor-pointer h-100 border-0 bg-component hover-bg-light position-relative overflow-hidden rounded-4 text-decoration-none"
										data-toggle-ai-tab="aiChatNew"
										data-toggle-ai-input="Create a concise and professional response for any situation."
									>
										<div className="card-body p-4">
											<div className="mb-3 d-flex">
												<iconify-icon
													icon="solar:chat-square-arrow-bold-duotone"
													className="fs-40px text-body"
												></iconify-icon>
											</div>
											<div className="text-secondary fw-bold small">Draft a Quick Reply</div>
											<div className="fs-6 fw-bold">
												Create a concise and professional response for any situation.
											</div>
										</div>
									</a>
								</div>
								<div className="col-sm-6 col-xl-3">
									<a
										className="card cursor-pointer h-100 border-0 bg-component hover-bg-light position-relative overflow-hidden rounded-4 text-decoration-none"
										data-toggle-ai-tab="aiChatNew"
										data-toggle-ai-input="Break down complex ideas into simple, easy-to-understand explanations."
									>
										<div className="card-body p-4">
											<div className="mb-3 d-flex">
												<iconify-icon
													icon="solar:layers-minimalistic-bold-duotone"
													className="fs-40px text-body"
												></iconify-icon>
											</div>
											<div className="text-secondary fw-bold small">Simplify This</div>
											<div className="fs-6 fw-bold">
												Break down complex ideas into simple, easy-to-understand explanations.
											</div>
										</div>
									</a>
								</div>
								<div className="col-sm-6 col-xl-3">
									<a
										className="card cursor-pointer h-100 border-0 bg-component hover-bg-light position-relative overflow-hidden rounded-4 text-decoration-none"
										data-toggle-ai-tab="aiChatNew"
										data-toggle-ai-input="Write and troubleshoot code with smart fixes for tricky errors."
									>
										<div className="card-body p-4">
											<div className="mb-3 d-flex">
												<iconify-icon
													icon="solar:code-bold-duotone"
													className="fs-40px text-body"
												></iconify-icon>
											</div>
											<div className="text-secondary fw-bold small">Code & Debug</div>
											<div className="fs-6 fw-bold">
												Write and troubleshoot code with smart fixes for tricky errors.
											</div>
										</div>
									</a>
								</div>
								<div className="col-sm-6 col-xl-3">
									<a
										className="card cursor-pointer h-100 border-0 bg-component hover-bg-light position-relative overflow-hidden rounded-4 text-decoration-none"
										data-toggle-ai-tab="aiChatNew"
										data-toggle-ai-input="Generate fresh ideas for projects, names, or creative solutions."
									>
										<div className="card-body p-4">
											<div className="mb-3 d-flex">
												<iconify-icon
													icon="solar:lightbulb-bolt-bold-duotone"
													className="fs-40px text-body"
												></iconify-icon>
											</div>
											<div className="text-secondary fw-bold small">Brainstorm Anything</div>
											<div className="fs-6 fw-bold">
												Generate fresh ideas for projects, names, or creative solutions.
											</div>
										</div>
									</a>
								</div>
								<div className="col-sm-6 col-xl-3">
									<a
										className="card cursor-pointer h-100 border-0 bg-component hover-bg-light position-relative overflow-hidden rounded-4 text-decoration-none"
										data-toggle-ai-tab="aiChatNew"
										data-toggle-ai-input="Craft emails, reports, or social posts in a polished, clear tone."
									>
										<div className="card-body p-4">
											<div className="mb-3 d-flex">
												<iconify-icon
													icon="solar:pen-2-bold-duotone"
													className="fs-40px text-body"
												></iconify-icon>
											</div>
											<div className="text-secondary fw-bold small">Write It for Me</div>
											<div className="fs-6 fw-bold">
												Craft emails, reports, or social posts in a polished, clear tone.
											</div>
										</div>
									</a>
								</div>
							</div>
						</div>
					</div>
					<div id="aiChatPrev" className="h-100 tab-pane fade">
						<div data-scrollbar="true" data-height="100%" className="px-lg-5 px-3 py-lg-4 py-3 small">
							<div className="d-flex justify-content-end align-items-end mb-3">
								<div className="rounded-4 px-3 py-2 bg-component text-body fw-bold">
									Can you generate some content for social media captions?
								</div>
								<div>
									<div className="w-30px h-30px my-2px ms-2 fs-20px me-2 rounded-circle text-white bg-theme-gradient d-flex align-items-center justify-content-center">
										S
									</div>
								</div>
							</div>
							<div className="mb-3">
								<div className="d-flex justify-content-start align-items-end">
									<div className="rounded-4 px-3 py-2 bg-component text-body fw-bold mw-75">
										<div>Absolutely! What’s the topic or product?</div>
									</div>
								</div>
								<div className="d-flex">
									<div className="d-flex flex-wrap w-100 p-2 opacity-75">
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-copy"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-thumbs-up"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-thumbs-down"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="fa fa-fw fa-microphone"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration">
											<i className="fa fa-fw fa-arrow-rotate-right"></i>
										</a>
									</div>
								</div>
							</div>
							<div className="d-flex justify-content-end align-items-end mb-3">
								<div className="rounded-4 px-3 py-2 bg-component text-body fw-bold">
									Let’s say… a coffee shop promotion.
								</div>
								<div>
									<div className="w-30px h-30px my-2px ms-2 fs-20px me-2 rounded-circle text-white bg-theme-gradient d-flex align-items-center justify-content-center">
										S
									</div>
								</div>
							</div>
							<div className="mb-3">
								<div className="d-flex justify-content-start align-items-end">
									<div className="rounded-4 px-3 py-2 bg-component text-body fw-bold mw-75">
										<div>How about these:</div>
										<ol className="my-2 ps-4">
											<li>Start your day with the perfect brew – 20% off all lattes today!</li>
											<li>Coffee first, everything else later. Grab your cup now!</li>
											<li>A little caffeine, a lot of happiness. Visit us for your daily fix!</li>
										</ol>
										<div>Need more options?</div>
									</div>
								</div>
								<div className="d-flex">
									<div className="d-flex flex-wrap w-100 p-2 opacity-75">
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-copy"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-thumbs-up"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-thumbs-down"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="fa fa-fw fa-microphone"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration">
											<i className="fa fa-fw fa-arrow-rotate-right"></i>
										</a>
									</div>
								</div>
							</div>
							<div className="d-flex justify-content-end align-items-end mb-3">
								<div className="rounded-4 px-3 py-2 bg-component text-body fw-bold">
									How can I fetch API data in Vue.js?
								</div>
								<div>
									<div className="w-30px h-30px my-2px ms-2 fs-20px me-2 rounded-circle text-white bg-theme-gradient d-flex align-items-center justify-content-center">
										S
									</div>
								</div>
							</div>
							<div className="mb-3">
								<div className="d-flex justify-content-start align-items-end">
									<div className="rounded-4 px-3 py-10px bg-component text-body fw-bold mw-75">
										Here’s a simple example:
										<div className="card card-borderless my-10px">
											<div className="card-header bg-body d-flex py-10px ">
												<span className="flex-1">vue</span>
												<span>
													<a href="#/" className="text-body text-decoration-none">
														<i className="far fa-copy"></i> Copy
													</a>
												</span>
											</div>
											<div className="hljs-wrapper bg-light rounded-top-0">
												<pre>
													<Highlight className="html py-10px">{aiCodeExample}</Highlight>
												</pre>
											</div>
										</div>
										Would you like me to explain how this works?
									</div>
								</div>
								<div className="d-flex">
									<div className="d-flex flex-wrap w-100 p-2 opacity-75">
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-copy"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-thumbs-up"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="far fa-fw fa-thumbs-down"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration me-2">
											<i className="fa fa-fw fa-microphone"></i>
										</a>
										<a href="#/" className="link-secondary text-decoration">
											<i className="fa fa-fw fa-arrow-rotate-right"></i>
										</a>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div id="aiChatNew" className="h-100 tab-pane fade">
						<div className="px-lg-5 px-3 py-lg-4 py-3 small" data-scrollbar="true" data-height="100%">
							<div id="aiChatNewContent" data-mode="default"></div>
						</div>
					</div>
				</div>
				<div>
					<div className="px-lg-4 px-3 py-3">
						<div className="position-relative mx-lg-4 h-100">
							<div className="position-absolute start-0 bottom-0 px-10px d-flex gap-1 py-2px">
								<button
									type="button"
									className="btn link-secondary fs-4 d-inline-flex px-0 ps-5px py-2"
								>
									<iconify-icon icon="solar:add-circle-linear"></iconify-icon>
								</button>
								<button
									type="button"
									className="btn link-secondary fs-4 d-inline-flex px-0 ps-5px py-2"
								>
									<iconify-icon icon="solar:global-linear"></iconify-icon>
								</button>
								<button
									type="button"
									className="btn link-secondary fs-4 d-inline-flex px-0 ps-5px py-2"
								>
									<iconify-icon icon="solar:microphone-large-linear"></iconify-icon>
								</button>
							</div>
							<input
								type="text"
								className="form-control form-control-lg fs-6 fw-semibold rounded-4 pb-80px pt-10px"
								autocomplete="off"
								id="userInput"
								placeholder="Ask anything"
							/>
							<div className="position-absolute end-0 bottom-0 p-2">
								<button id="sendButton" className="btn btn-secondary btn-lg btn-icon fs-6">
									<i className="fa fa-arrow-right"></i>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export default AIChat;
